import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Badge, Banner, Box, BlockStack, Button, Card, InlineStack,
    ProgressBar, Text, TextField, Toast,
} from "@shopify/polaris";
import { useDispatch, useSelector } from "react-redux";
import { ApiCall } from "../../helper/axios";
import { updateModalStatus } from "../../redux/slice/commonSlice";
// Step 2 visual guide, shown only in the install-time onboarding (modal).
// Replace this one file with a real annotated screenshot any time - the import
// path stays the same, so no code change is needed.
import appEmbedGuide from "../../assets/images/onboarding-app-embed.svg";

const STEP_TOTAL = 2;

/*
 * Premium 2-step onboarding for the Currency Converter.
 *
 *   Step 1 - Configure currency formatting (wrap money in <span class="money">)
 *   Step 2 - Enable the app embed in the live theme
 *
 * Setup is auto-detected from the server (POST /validate-integration, which now
 * returns per-step status: { valid, currencyValid, appEmbedValid }). We re-check
 * on mount and whenever the merchant returns to the tab, so there is no manual
 * "confirm" - the app verifies for them. When both steps pass we show a success
 * screen; "View dashboard" / "Customize" hand control back to HomePageComponent.
 *
 * Used two ways:
 *   - modal=true  -> full onboarding takeover (shown while setup is invalid)
 *   - modal=false -> the "Store Integration" tab (status reference, any time)
 */
const StoreIntegration = ({ modal = false, onComplete, onCustomize }) => {
    const { shop_data, token } = useSelector((store) => store.commonData.store_data);
    const dispatch = useDispatch();

    const [snippet, setSnippet] = useState("<span class=money>Rs. {{amount}}</span>");
    const [currencyUrl, setCurrencyUrl] = useState('');
    const [appEmbedUrl, setAppEmbedUrl] = useState('');

    const [status, setStatus] = useState(null); // { valid, currencyValid, appEmbedValid }
    const [checking, setChecking] = useState(true); // first load + silent re-checks
    const [verifying, setVerifying] = useState(false); // explicit "Verify setup"
    const [toast, setToast] = useState('');
    const debounceRef = useRef(null);

    // Build the money snippet + deep links from the shop.
    useEffect(() => {
        if (!shop_data) return;
        const mf = shop_data.money_format;
        if (mf) {
            setSnippet(mf.includes('span') && mf.includes('money') ? mf : `<span class=money>${mf}</span>`);
        }
        if (shop_data.store_name) {
            setCurrencyUrl(`https://${shop_data.store_name}/admin/settings/general`);
            setAppEmbedUrl(`https://${shop_data.store_name}/admin/themes/current/editor?context=apps&appEmbed=${process.env.EXTENSION_UUID}/${process.env.EXTENSION_BLOCK_NAME}`);
        }
    }, [shop_data]);

    const runValidation = useCallback(async ({ silent = false } = {}) => {
        if (!token) return null;
        if (silent) setChecking(true); else setVerifying(true);
        try {
            const res = await ApiCall('POST', '/validate-integration', {}, { authentication: token });
            const d = res?.data?.data;
            // Tolerant of both the new object shape and the old single boolean.
            const next = (d && typeof d === 'object')
                ? { valid: !!d.valid, currencyValid: !!d.currencyValid, appEmbedValid: !!d.appEmbedValid }
                : { valid: !!d, currencyValid: !!d, appEmbedValid: !!d };
            setStatus(next);
            return next;
        } catch (e) {
            return null;
        } finally {
            setChecking(false);
            setVerifying(false);
        }
    }, [token]);

    // Auto-detect on mount.
    useEffect(() => { runValidation({ silent: true }); }, [runValidation]);

    // Auto re-check when the merchant comes back to the app (e.g. after doing the
    // steps in Shopify admin), until everything is set up - no manual confirm.
    useEffect(() => {
        const onReturn = () => {
            if (status?.valid) return;
            if (document.visibilityState && document.visibilityState !== 'visible') return;
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => runValidation({ silent: true }), 400);
        };
        window.addEventListener('focus', onReturn);
        document.addEventListener('visibilitychange', onReturn);
        return () => {
            window.removeEventListener('focus', onReturn);
            document.removeEventListener('visibilitychange', onReturn);
            clearTimeout(debounceRef.current);
        };
    }, [runValidation, status]);

    const copy = (text) => {
        try { navigator.clipboard.writeText(text); setToast('Copied to clipboard'); } catch (e) { /* noop */ }
    };

    const onVerify = async () => {
        const next = await runValidation({ silent: false });
        if (next && !next.valid) {
            setToast(next.currencyValid || next.appEmbedValid
                ? "Almost there - finish the remaining step, then verify again."
                : "Not detected yet - complete both steps, then verify again.");
        }
    };

    const currencyValid = !!status?.currencyValid;
    const appEmbedValid = !!status?.appEmbedValid;
    const allValid = !!status?.valid;
    const completed = (currencyValid ? 1 : 0) + (appEmbedValid ? 1 : 0);
    const progress = Math.round((completed / STEP_TOTAL) * 100);
    const loadingFirst = checking && status === null;

    // Onboarding complete -> skip the success screen and go straight to the
    // dashboard (close the takeover). Fires once; the modal then unmounts.
    useEffect(() => {
        if (modal && allValid) {
            if (onComplete) onComplete();
            else dispatch(updateModalStatus(false));
        }
    }, [modal, allValid, onComplete, dispatch]);

    const statusBadge = (valid) =>
        loadingFirst ? <Badge tone="info" progress="incomplete">Checking…</Badge>
            : valid ? <Badge tone="success" progress="complete">Completed</Badge>
                : <Badge tone="attention" progress="incomplete">Action needed</Badge>;

    const numCircle = (valid, n) => (
        <span className={`hw-onb-num${valid ? ' done' : ''}`}>{valid ? '✓' : n}</span>
    );

    const toastMarkup = toast ? <Toast content={toast} onDismiss={() => setToast('')} /> : null;

    const styleTag = (
        <style>{`
            .hw-onb { max-width: 940px; margin: 0 auto; }
            .hw-onb-hero {
                background: linear-gradient(135deg, var(--p-color-bg-surface) 0%, var(--p-color-bg-surface-secondary) 100%);
                border: 1px solid var(--p-color-border);
                border-radius: var(--p-border-radius-300);
                padding: 28px;
            }
            .hw-onb-num {
                display:inline-flex; align-items:center; justify-content:center;
                width:30px; height:30px; border-radius:50%; flex:0 0 auto;
                background: var(--p-color-bg-fill-brand, #303030); color:#fff;
                font-weight:600; font-size:14px;
            }
            .hw-onb-num.done { background: var(--p-color-bg-fill-success, #29845a); }
            .hw-onb-celebrate { font-size:56px; line-height:1; }
            .hw-onb-narrow { max-width:520px; }
            .hw-onb-shot img { display:block; width:100%; height:auto; border:1px solid var(--p-color-border); border-radius: var(--p-border-radius-300); }
            .hw-onb-key { display:inline-flex; align-items:center; justify-content:center; width:18px; height:18px; border-radius:50%; background:#1a73e8; color:#fff; font-size:11px; font-weight:700; margin-right:6px; vertical-align:middle; }
        `}</style>
    );

    // While the takeover is up and setup just became valid, the effect above
    // redirects to the dashboard - render nothing in that brief moment.
    if (allValid && modal) return null;

    // ---- Onboarding / status flow ---------------------------------------------
    return (
        <>
            {styleTag}
            <div className="hw-onb" style={{ marginTop: modal ? 12 : 28 }}>
                <BlockStack gap="500">
                    {/* Hero + progress */}
                    <div className="hw-onb-hero">
                        <BlockStack gap="400">
                            <BlockStack gap="200">
                                <Text as="h1" variant="heading2xl">Get your currency converter ready</Text>
                                <Text as="p" variant="bodyLg" tone="subdued">
                                    Complete 2 quick steps to start showing prices in your customers’ local currencies.
                                </Text>
                            </BlockStack>
                            <BlockStack gap="150">
                                <InlineStack align="space-between" blockAlign="center">
                                    <Text as="span" variant="headingSm">
                                        {allValid ? 'All steps complete' : `${completed} of ${STEP_TOTAL} steps complete`}
                                    </Text>
                                    <Text as="span" variant="bodySm" tone="subdued">{progress}% complete</Text>
                                </InlineStack>
                                <ProgressBar progress={progress} size="small" tone={allValid ? 'success' : 'primary'} />
                            </BlockStack>
                        </BlockStack>
                    </div>

                    {allValid && !modal ? (
                        <Banner tone="success" title="Your store is fully set up">
                            Both steps are complete. The currency converter is live on your storefront.
                        </Banner>
                    ) : null}

                    {/* Step 1 - Configure currency formatting */}
                    <Card>
                        <BlockStack gap="400">
                            <InlineStack align="space-between" blockAlign="center" wrap={false} gap="400">
                                <InlineStack gap="300" blockAlign="center" wrap={false}>
                                    {numCircle(currencyValid, 1)}
                                    <BlockStack gap="050">
                                        <Text as="h2" variant="headingMd">Configure currency formatting</Text>
                                        <Text as="span" variant="bodySm" tone="subdued">
                                            Lets the app detect and convert every price across your store.
                                        </Text>
                                    </BlockStack>
                                </InlineStack>
                                {statusBadge(currencyValid)}
                            </InlineStack>

                            <Box background="bg-surface-secondary" borderRadius="300" padding="400">
                                <BlockStack gap="300">
                                    <Text as="span" variant="bodySm" tone="subdued">
                                        In Shopify: <b>Settings → General → Store currency → Change formatting</b>. Paste the snippet
                                        below into <b>both</b> the “HTML with currency” and “HTML without currency” fields, then Save.
                                    </Text>
                                    <TextField
                                        label="HTML with currency"
                                        readOnly
                                        value={snippet}
                                        autoComplete="off"
                                        connectedRight={<Button onClick={() => copy(snippet)}>Copy</Button>}
                                    />
                                    <TextField
                                        label="HTML without currency"
                                        readOnly
                                        value={snippet}
                                        autoComplete="off"
                                        connectedRight={<Button onClick={() => copy(snippet)}>Copy</Button>}
                                    />
                                </BlockStack>
                            </Box>

                            <InlineStack gap="300">
                                <Button variant="primary" url={currencyUrl} external>Open currency settings</Button>
                            </InlineStack>
                        </BlockStack>
                    </Card>

                    {/* Step 2 - Enable app embed */}
                    <Card>
                        <BlockStack gap="400">
                            <InlineStack align="space-between" blockAlign="center" wrap={false} gap="400">
                                <InlineStack gap="300" blockAlign="center" wrap={false}>
                                    {numCircle(appEmbedValid, 2)}
                                    <BlockStack gap="050">
                                        <Text as="h2" variant="headingMd">Enable the app embed</Text>
                                        <Text as="span" variant="bodySm" tone="subdued">
                                            Turns the converter on inside your live theme.
                                        </Text>
                                    </BlockStack>
                                </InlineStack>
                                {statusBadge(appEmbedValid)}
                            </InlineStack>

                            <Box background="bg-surface-secondary" borderRadius="300" padding="400">
                                <Text as="span" variant="bodySm" tone="subdued">
                                    Open the theme editor, switch on <b>Currency Converter</b> under <b>App embeds</b>, then click
                                    <b> Save</b> in the top-right corner.
                                </Text>
                            </Box>

                            {modal ? (
                                <BlockStack gap="200">
                                    <div className="hw-onb-shot">
                                        <img src={appEmbedGuide} alt="Where to find and switch on the Currency Converter app embed in your theme editor, then Save" />
                                    </div>
                                    <InlineStack gap="400" wrap>
                                        <Text as="span" variant="bodySm" tone="subdued"><span className="hw-onb-key">1</span>Switch on Currency Converter</Text>
                                        <Text as="span" variant="bodySm" tone="subdued"><span className="hw-onb-key">2</span>Click Save (top-right)</Text>
                                    </InlineStack>
                                </BlockStack>
                            ) : null}

                            <InlineStack gap="300">
                                <Button variant="primary" url={appEmbedUrl} external>Open theme editor</Button>
                            </InlineStack>
                        </BlockStack>
                    </Card>

                    {/* Verify - only while setup is still incomplete */}
                    {!allValid ? (
                        <Box paddingBlockStart="100" paddingBlockEnd={modal ? "200" : "400"}>
                            <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                                <Text as="span" variant="bodySm" tone="subdued">
                                    We check automatically when you come back — or verify now.
                                </Text>
                                <Button onClick={onVerify} loading={verifying} variant="primary" size="large">
                                    Verify setup
                                </Button>
                            </InlineStack>
                        </Box>
                    ) : null}

                    {modal ? (
                        <Box paddingBlockEnd="400">
                            <InlineStack align="center" gap="150" blockAlign="center">
                                <Text as="span" variant="bodySm" tone="subdued">Need a hand setting up?</Text>
                                <Button variant="plain" url="https://www.huptechweb.com/support" external>Contact support</Button>
                            </InlineStack>
                        </Box>
                    ) : null}
                </BlockStack>
            </div>
            {toastMarkup}
        </>
    );
};

export default StoreIntegration;
